import { ArrowRight, MapTrifold, Pause, Play } from "@phosphor-icons/react";
import { lazy, Suspense, useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { InlineError } from "../components/InlineError";
import { MapLegend } from "../components/MapLegend";
import { MunicipalitySearch } from "../components/MunicipalitySearch";
import { PeriodControl } from "../components/PeriodControl";
import { tariffDataSource } from "../data/staticDataSource";
import { track } from "../lib/analytics";
import { formatRate } from "../lib/format";
import { useDocumentMetadata } from "../lib/useDocumentMetadata";
import type { Manifest, MuniSummary } from "../types/tariffs";

const NationalMap = lazy(() => import("../components/NationalMap").then((module) => ({ default: module.NationalMap })));

export function LandingPage() {
  useDocumentMetadata("Watt It Costs | Municipal electricity tariffs", "Explore published residential electricity tariffs across South African municipalities.");
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [manifest, setManifest] = useState<Manifest | null>(null);
  const [municipalities, setMunicipalities] = useState<MuniSummary[]>([]);
  const [snapshot, setSnapshot] = useState<Record<string, number>>({});
  const [error, setError] = useState("");
  const [showMap, setShowMap] = useState(false);
  const [hoveredCode, setHoveredCode] = useState<string>();
  const [playing, setPlaying] = useState(false);
  const periodId = params.get("period") || manifest?.latestPeriodId || "2025-26";
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    let cancelled = false;
    Promise.all([tariffDataSource.getManifest(), tariffDataSource.getIndex()])
      .then(([nextManifest, index]) => {
        if (cancelled) return;
        setManifest(nextManifest);
        setMunicipalities(index);
      })
      .catch(() => { if (!cancelled) setError("The tariff index is unavailable. Check your connection and retry."); });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!manifest) return;
    const mappedPeriods = manifest.periods.filter((period) => period.mapped);
    if (params.get("period") && !mappedPeriods.some((period) => period.id === params.get("period"))) {
      setParams({ period: mappedPeriods.at(-1)?.id ?? manifest.latestPeriodId }, { replace: true });
    }
  }, [manifest, params, setParams]);

  useEffect(() => {
    let cancelled = false;
    tariffDataSource.getPeriodSnapshot(periodId)
      .then((nextSnapshot) => { if (!cancelled) setSnapshot(nextSnapshot); })
      .catch(() => { if (!cancelled) setSnapshot({}); });
    return () => { cancelled = true; };
  }, [periodId]);

  useEffect(() => {
    const media = window.matchMedia("(min-width: 901px)");
    if (media.matches) setShowMap(true);
    const enableDesktopMap = (event: MediaQueryListEvent) => {
      if (event.matches) setShowMap(true);
    };
    media.addEventListener("change", enableDesktopMap);
    return () => media.removeEventListener("change", enableDesktopMap);
  }, []);

  useEffect(() => {
    if (!playing || !manifest) return;
    const timer = window.setInterval(() => {
      const mappedPeriods = manifest.periods.filter((period) => period.mapped);
      const currentIndex = mappedPeriods.findIndex((period) => period.id === periodId);
      const nextPeriod = mappedPeriods[(currentIndex + 1) % mappedPeriods.length];
      setParams({ period: nextPeriod.id }, { replace: true });
    }, 1200);
    return () => window.clearInterval(timer);
  }, [manifest, periodId, playing, setParams]);

  const ranked = [...municipalities].sort((a, b) => (snapshot[a.code] ?? Infinity) - (snapshot[b.code] ?? Infinity));

  return (
    <>
      <section className="landing-intro">
        <div className="page-shell intro-grid">
          <div className="intro-copy">
            <p className="eyebrow">South African municipal electricity</p>
            <h1>See what power costs where you live.</h1>
            <p className="intro-deck">Explore published residential tariffs, compare plans, and understand how monthly usage changes the estimate.</p>
          </div>
          <div className="search-panel">
            <MunicipalitySearch />
            <div className="quick-links" aria-label="Popular municipalities">
              <span>Quick entry</span>
              <Link to="/m/CPT">Cape Town</Link>
              <Link to="/m/JHB">Johannesburg</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="national-section" aria-labelledby="national-heading">
        <div className="page-shell">
          <div className="section-heading-row">
            <div>
              <h2 id="national-heading">National comparison</h2>
              <p>Representative residential plan at {manifest?.referenceUsageKwh ?? 600} kWh/month. Your usage setting does not change this view.</p>
            </div>
            {manifest && (
              <div className="period-actions">
                <PeriodControl
                  value={periodId}
                  periods={manifest.periods.filter((period) => period.mapped)}
                  onChange={(nextPeriod) => { setParams({ period: nextPeriod }, { replace: true }); track("map_period_scrubbed", { period_id: nextPeriod, view: "explorer" }); }}
                />
                <button
                  className="period-play-button"
                  type="button"
                  aria-pressed={playing}
                  disabled={reducedMotion}
                  title={reducedMotion ? "Period playback is disabled by your reduced-motion preference" : undefined}
                  onClick={() => setPlaying((value) => !value)}
                >
                  {playing ? <Pause weight="fill" /> : <Play weight="fill" />}
                  {playing ? "Pause" : "Play periods"}
                </button>
              </div>
            )}
          </div>

          {error ? <InlineError message={error} onRetry={() => window.location.reload()} /> : (
            <>
              <button className="map-toggle-button" type="button" aria-expanded={showMap} aria-controls="national-explorer" onClick={() => setShowMap((value) => !value)}>
                <MapTrifold /> {showMap ? "Hide map" : "Open interactive map"}
              </button>
              <div id="national-explorer" className={showMap ? "explorer-grid has-map" : "explorer-grid list-only"}>
                {showMap && manifest && (
                  <div className="map-panel">
                    <div className="map-context">Residential electricity at {manifest.referenceUsageKwh} kWh/month | {periodId.replace("-", "/")}</div>
                    <Suspense fallback={<div className="map-loading-static" aria-busy="true"><span className="skeleton" /><p>Loading interactive map</p></div>}>
                      <NationalMap
                        snapshot={snapshot}
                        municipalities={municipalities}
                        domain={manifest.choroplethDomainC}
                        hoveredCode={hoveredCode}
                        reducedMotion={reducedMotion}
                        onHoverCode={setHoveredCode}
                        onSelectCode={(code) => { track("municipality_selected", { municipality_code: code, source: "map" }); navigate(`/m/${code}?period=${periodId}&usage=600`); }}
                      />
                    </Suspense>
                    <MapLegend domain={manifest.choroplethDomainC} referenceUsageKwh={manifest.referenceUsageKwh} />
                    <span className="fixture-label">MDB boundary geometry · tariff coverage shown separately</span>
                  </div>
                )}
                <div className="ranking-panel">
                <div className="ranking-header">
                  <span>Municipality</span>
                  <span>Effective rate</span>
                </div>
                <ol className="ranking-list">
                  {ranked.map((municipality, index) => (
                    <li key={municipality.code}>
                      <Link
                        className={hoveredCode === municipality.code ? "is-linked-hover" : ""}
                        to={`/m/${municipality.code}?period=${periodId}&usage=600`}
                        onMouseEnter={() => setHoveredCode(municipality.code)}
                        onMouseLeave={() => setHoveredCode(undefined)}
                        onFocus={() => setHoveredCode(municipality.code)}
                        onBlur={() => setHoveredCode(undefined)}
                        onClick={() => track("municipality_selected", { municipality_code: municipality.code, source: "ranked_list" })}
                      >
                        <span className="rank-number">{String(index + 1).padStart(2, "0")}</span>
                        <span className="rank-name"><strong>{municipality.name}</strong><small>{municipality.province}</small></span>
                        <span className="rank-rate">{snapshot[municipality.code] === undefined ? "No verified data" : formatRate(snapshot[municipality.code])}</span>
                        <ArrowRight aria-hidden="true" />
                      </Link>
                    </li>
                  ))}
                </ol>
                  {!municipalities.length && <div className="ranking-loading" aria-busy="true"><span className="skeleton" /><span className="skeleton" /></div>}
                </div>
              </div>
            </>
          )}
        </div>
      </section>

      <section className="explanation-section">
        <div className="page-shell explanation-grid">
          <h2>A rate is only part of the bill.</h2>
          <div>
            <p>Fixed charges, consumption blocks, VAT, and eligibility rules can change the final amount. Municipality pages show the calculation for every supported residential plan.</p>
            <Link className="inline-link" to="/about">Read the methodology <ArrowRight /></Link>
          </div>
        </div>
      </section>
    </>
  );
}
